import { BaseComponentImpl } from '../../../../types/implementations';
import { Building } from '../../../../types/protocols';
import { BaseType } from '../../../../types/Enums';
import { ComponentLifetime, TemplatesByType } from '../../TemplatesEditor/types';
import { BASE_TYPE_GROUPS } from '../../../../Helpers/baseTypeUtils';
import type { CostSource, MaintenancePlan } from '../../../../Helpers/maintenanceCostUtils';
import {
    ComponentSchedule,
    ResolvedActivity,
    ResolvedReplacement,
    resolveComponentSchedule,
    templateLookup
} from '../../../../Helpers/maintenanceSchedule';

const COST_SOURCE_SHORT: Record<CostSource, string> = { template: 'schablon', customRate: 'anpassad schablon', custom: 'offert' };

/**
 * What activating a draft would change in the active plan.
 *
 * Both sides are compared as resolved schedules (`resolveComponentSchedule`),
 * the draft as activation would snapshot it, so a value the draft leaves to the
 * template is not a difference merely because the active plan stores it.
 * Items are a component's replacement ("Byte") and its activities by template
 * id. An item is listed only when it occurs inside the window in either plan,
 * and only when it differs; the totals are the window's costs in today's prices,
 * computed as the preview computes them.
 */

export type DiffKind = 'added' | 'removed' | 'changed';

export interface FieldChange {
    field: 'date' | 'year' | 'interval' | 'cost' | 'investment';
    label: string;
    from: string;
    to: string;
}

export interface ItemDiff {
    /** `replacement` or the template id */
    key: string;
    label: string;
    kind: DiffKind;
    /** Field by field, for a changed item */
    changes: FieldChange[];
    /** The item as it is, for an added or removed one */
    description?: string;
    /** Draft cost minus active cost inside the window */
    costDelta: number;
}

export interface ComponentDiff {
    componentKey: string;
    componentName: string;
    kind: DiffKind;
    items: ItemDiff[];
    costDelta: number;
}

export interface DiffGroup {
    key: string;
    label: string;
    components: ComponentDiff[];
}

export interface PlanDiff {
    groups: DiffGroup[];
    activeTotal: number;
    draftTotal: number;
    /** Changed items over every component */
    changeCount: number;
}

export interface PlanDiffOptions {
    active: MaintenancePlan;
    draft: MaintenancePlan;
    building: Building;
    componentLifetimes: Record<string, ComponentLifetime>;
    maintenanceTemplates: TemplatesByType;
    /** specificType → baseType */
    baseTypeOf: (componentType: string) => BaseType | undefined;
    yearsToShow: number;
    now?: Date;
}

const INTERVAL_UNITS: Record<string, string> = { days: 'dagar', weeks: 'veckor', months: 'månader', years: 'år' };

const formatKr = (amount: number): string => `${Math.round(amount).toLocaleString('sv-SE')} kr`;
const formatCost = (amount: number, source: CostSource): string => `${formatKr(amount)} (${COST_SOURCE_SHORT[source]})`;
const formatInterval = (value: number, unit: string): string => `var ${value} ${INTERVAL_UNITS[unit] ?? unit}`;
const formatPercent = (value: number | undefined): string => `${value ?? 0} %`;

const replacementCost = (replacement: ResolvedReplacement | null | undefined): number =>
    replacement ? replacement.cost * replacement.years.length : 0;
const activityCost = (activity: ResolvedActivity | undefined): number =>
    activity ? activity.cost * activity.dates.length : 0;

const sameCost = (a: number, b: number): boolean => Math.round(a) === Math.round(b);

const diffReplacement = (from: ResolvedReplacement | null, to: ResolvedReplacement | null): ItemDiff | null => {
    if (!from?.years.length && !to?.years.length) return null;
    const costDelta = replacementCost(to) - replacementCost(from);
    const describe = (r: ResolvedReplacement) => `${r.firstYear} · ${formatCost(r.cost, r.costSource)}`;
    if (!from) return { key: 'replacement', label: 'Byte', kind: 'added', changes: [], description: describe(to!), costDelta };
    if (!to) return { key: 'replacement', label: 'Byte', kind: 'removed', changes: [], description: describe(from), costDelta };

    const changes: FieldChange[] = [];
    if (from.firstYear !== to.firstYear) {
        changes.push({ field: 'year', label: 'Bytesår', from: String(from.firstYear), to: String(to.firstYear) });
    }
    if (!sameCost(from.cost, to.cost) || from.costSource !== to.costSource) {
        changes.push({ field: 'cost', label: 'Kostnad', from: formatCost(from.cost, from.costSource), to: formatCost(to.cost, to.costSource) });
    }
    if ((from.investmentPercentage ?? 0) !== (to.investmentPercentage ?? 0)) {
        changes.push({ field: 'investment', label: 'Investeringsandel', from: formatPercent(from.investmentPercentage), to: formatPercent(to.investmentPercentage) });
    }
    return changes.length > 0 ? { key: 'replacement', label: 'Byte', kind: 'changed', changes, costDelta } : null;
};

const diffActivity = (from: ResolvedActivity | undefined, to: ResolvedActivity | undefined): ItemDiff | null => {
    if (!from?.dates.length && !to?.dates.length) return null;
    const either = (to ?? from)!;
    const label = either.template.name;
    const key = either.templateId;
    const costDelta = activityCost(to) - activityCost(from);
    const describe = (a: ResolvedActivity) =>
        `${a.firstDate} · ${formatInterval(a.intervalValue, a.intervalType)} · ${formatCost(a.cost, a.costSource)}`;
    if (!from) return { key, label, kind: 'added', changes: [], description: describe(to!), costDelta };
    if (!to) return { key, label, kind: 'removed', changes: [], description: describe(from), costDelta };

    const changes: FieldChange[] = [];
    if (from.firstDate !== to.firstDate) {
        changes.push({ field: 'date', label: 'Nästa datum', from: from.firstDate, to: to.firstDate });
    }
    if (from.intervalValue !== to.intervalValue || from.intervalType !== to.intervalType) {
        changes.push({
            field: 'interval',
            label: 'Intervall',
            from: formatInterval(from.intervalValue, from.intervalType),
            to: formatInterval(to.intervalValue, to.intervalType)
        });
    }
    if (!sameCost(from.cost, to.cost) || from.costSource !== to.costSource) {
        changes.push({ field: 'cost', label: 'Kostnad', from: formatCost(from.cost, from.costSource), to: formatCost(to.cost, to.costSource) });
    }
    if ((from.investmentPercentage ?? 0) !== (to.investmentPercentage ?? 0)) {
        changes.push({ field: 'investment', label: 'Investeringsandel', from: formatPercent(from.investmentPercentage), to: formatPercent(to.investmentPercentage) });
    }
    return changes.length > 0 ? { key, label, kind: 'changed', changes, costDelta } : null;
};

const scheduleCost = (schedule: ComponentSchedule | null): number =>
    schedule
        ? replacementCost(schedule.replacement) + schedule.activities.reduce((sum, a) => sum + activityCost(a), 0)
        : 0;

export const computePlanDiff = (options: PlanDiffOptions): PlanDiff => {
    const { active, draft, building, componentLifetimes, maintenanceTemplates, baseTypeOf, yearsToShow } = options;
    const startYear = (options.now ?? new Date()).getFullYear();
    const window = { startYear, endYear: startYear + yearsToShow };
    const findTemplate = templateLookup(maintenanceTemplates);

    const byBaseType = new Map<string, ComponentDiff[]>();
    let activeTotal = 0;
    let draftTotal = 0;
    let changeCount = 0;

    Object.values(building.model?.structure?.components || {}).forEach(componentData => {
        const componentType = componentData?.specificType?.value;
        if (!componentType) return;
        const activeEntry = active.components?.[componentData.key];
        const draftEntry = draft.components?.[componentData.key];
        const inActive = !!activeEntry && activeEntry.excluded !== true;
        const inDraft = !!draftEntry && draftEntry.excluded !== true;
        if (!inActive && !inDraft) return;

        const component = new BaseComponentImpl(componentData);
        const resolve = (entry: typeof activeEntry, isDraft: boolean) => resolveComponentSchedule({
            component,
            entry: entry!,
            building,
            lifetime: componentLifetimes[componentType],
            typeTemplates: maintenanceTemplates[componentType] || [],
            findTemplate,
            window,
            isDraft
        });
        const from = inActive ? resolve(activeEntry, false) : null;
        const to = inDraft ? resolve(draftEntry, true) : null;
        activeTotal += scheduleCost(from);
        draftTotal += scheduleCost(to);

        const items: ItemDiff[] = [];
        const replacement = diffReplacement(from?.replacement ?? null, to?.replacement ?? null);
        if (replacement) items.push(replacement);

        const templateIds: string[] = [];
        [...(from?.activities ?? []), ...(to?.activities ?? [])].forEach(a => {
            if (!templateIds.includes(a.templateId)) templateIds.push(a.templateId);
        });
        templateIds.forEach(templateId => {
            const item = diffActivity(
                from?.activities.find(a => a.templateId === templateId),
                to?.activities.find(a => a.templateId === templateId)
            );
            if (item) items.push(item);
        });

        if (items.length === 0) return;
        changeCount += items.length;

        const baseType = baseTypeOf(componentType) ?? BaseType.Utility;
        const list = byBaseType.get(baseType) ?? [];
        list.push({
            componentKey: component.key,
            componentName: component.displayName(building),
            kind: !from ? 'added' : !to ? 'removed' : 'changed',
            items,
            costDelta: scheduleCost(to) - scheduleCost(from)
        });
        byBaseType.set(baseType, list);
    });

    const groups: DiffGroup[] = [];
    const grouped = new Set<string>();
    BASE_TYPE_GROUPS.forEach(group => {
        const components = group.baseTypes.flatMap(baseType => {
            grouped.add(baseType);
            return byBaseType.get(baseType) ?? [];
        });
        if (components.length === 0) return;
        components.sort((a, b) => a.componentName.localeCompare(b.componentName, 'sv'));
        groups.push({ key: group.key, label: group.label, components });
    });
    const ungrouped = Array.from(byBaseType.entries())
        .filter(([baseType]) => !grouped.has(baseType))
        .flatMap(([, components]) => components);
    if (ungrouped.length > 0) {
        ungrouped.sort((a, b) => a.componentName.localeCompare(b.componentName, 'sv'));
        groups.push({ key: 'other', label: 'Övrigt', components: ungrouped });
    }

    return { groups, activeTotal, draftTotal, changeCount };
};
